/**
 * Data Preview Component
 * 
 * Shows preview of parsed CSV data and column mapping results
 * Allows manual mapping of unmapped columns
 */

import React, { useState } from 'react';
import { AlertCircle, CheckCircle, ArrowRight, Settings, Eye, EyeOff } from 'lucide-react';
import { createManualMappingConfig, getAvailableFields } from './utils/dataMapper';

const DataPreview = ({ 
  parsedData, 
  mappingResult, 
  mappingConfidence,
  onMappingUpdate,
  onNext, 
  onBack, 
  disabled = false 
}) => {
  const [showFullPreview, setShowFullPreview] = useState(false);
  const [showManualMapping, setShowManualMapping] = useState(false);
  const [manualMappings, setManualMappings] = useState({});

  // Initialize manual mapping config for unmapped columns
  const manualMappingConfig = mappingResult ? 
    createManualMappingConfig(mappingResult.unmappedColumns) : null;

  const handleManualMappingChange = (csvColumn, selectedField) => {
    setManualMappings(prev => ({
      ...prev,
      [csvColumn]: selectedField
    }));
  };

  const applyManualMappings = () => {
    if (!mappingResult) return;

    const updatedMapping = { ...mappingResult.mapping };
    const updatedUnmapped = [];

    // Apply manual mappings
    Object.entries(manualMappings).forEach(([csvColumn, appField]) => {
      if (appField && appField !== '') {
        updatedMapping[csvColumn] = appField;
      } else {
        updatedUnmapped.push(csvColumn);
      }
    });

    // Update unmapped columns list
    mappingResult.unmappedColumns.forEach(column => {
      if (!manualMappings[column]) {
        updatedUnmapped.push(column);
      }
    });

    const updatedMappingResult = {
      ...mappingResult,
      mapping: updatedMapping,
      unmappedColumns: updatedUnmapped,
      mappedCount: Object.keys(updatedMapping).length,
      totalColumns: mappingResult.totalColumns
    };

    // Update confidence
    const mappingPercentage = (updatedMappingResult.mappedCount / updatedMappingResult.totalColumns) * 100;
    const updatedConfidence = {
      ...mappingConfidence,
      score: mappingPercentage,
      level: mappingPercentage >= 80 ? 'high' : mappingPercentage >= 50 ? 'medium' : 'low',
      mappedCount: updatedMappingResult.mappedCount,
      unmappedColumns: updatedUnmapped
    };

    onMappingUpdate(updatedMappingResult, updatedConfidence);
    setShowManualMapping(false);
  };

  const getConfidenceColor = (level) => {
    switch (level) {
      case 'high': return 'text-green-600 bg-green-50 border-green-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getConfidenceIcon = (level) => {
    switch (level) {
      case 'high': return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'medium': return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      case 'low': return <AlertCircle className="h-5 w-5 text-red-500" />;
      default: return <AlertCircle className="h-5 w-5 text-gray-500" />;
    }
  };

  const ManualMappingModal = () => {
    if (!showManualMapping || !manualMappingConfig) return null;

    const availableFields = getAvailableFields();
    const flatFields = [];
    
    // Flatten available fields for dropdown
    Object.entries(availableFields).forEach(([category, fields]) => {
      fields.forEach(field => {
        flatFields.push({
          value: field.value,
          label: `${category}: ${field.label}`,
          category: category
        });
      });
    });

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Manual Column Mapping</h3>
              <button
                onClick={() => setShowManualMapping(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Map unmapped columns to application fields. Leave blank to ignore columns.
            </p>
          </div>
          
          <div className="px-6 py-4 max-h-96 overflow-y-auto">
            <div className="space-y-4">
              {mappingResult.unmappedColumns.map(column => (
                <div key={column} className="flex items-center space-x-4 p-3 border rounded-lg">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CSV Column: <span className="font-semibold">{column}</span>
                    </label>
                    <select
                      value={manualMappings[column] || ''}
                      onChange={(e) => handleManualMappingChange(column, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">-- Select field to map to --</option>
                      {Object.entries(availableFields).map(([category, fields]) => (
                        <optgroup key={category} label={category}>
                          {fields.map(field => (
                            <option key={field.value} value={field.value}>
                              {field.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    {/* Show suggestion if available */}
                    {manualMappingConfig && manualMappingConfig.unmappedColumns && (
                      (() => {
                        const columnConfig = manualMappingConfig.unmappedColumns.find(c => c.csvColumn === column);
                        return columnConfig && columnConfig.suggestedMapping ? (
                          <p className="text-xs text-blue-600 mt-1">
                            Suggested: {availableFields[Object.keys(availableFields).find(cat => 
                              availableFields[cat].some(f => f.value === columnConfig.suggestedMapping)
                            )]?.find(f => f.value === columnConfig.suggestedMapping)?.label || columnConfig.suggestedMapping}
                          </p>
                        ) : null;
                      })()
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
            <button
              onClick={() => setShowManualMapping(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={applyManualMappings}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
            >
              Apply Mappings
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Asset Name Preview Component
  const AssetNamePreview = () => {
    if (!parsedData || !mappingResult) return null;

    // Find asset name column
    const assetNameColumn = Object.keys(mappingResult.mapping).find(col => 
      mappingResult.mapping[col] === 'assetName'
    );

    console.log('DEBUG - AssetNamePreview: Asset name column found:', assetNameColumn);
    console.log('DEBUG - AssetNamePreview: Available columns:', Object.keys(parsedData.data[0] || {}));

    // If no mapped column, try to find one manually
    let detectedColumn = assetNameColumn;
    if (!detectedColumn && parsedData.data.length > 0) {
      const csvKeys = Object.keys(parsedData.data[0]);
      detectedColumn = csvKeys.find(key => {
        const lowerKey = key.toLowerCase();
        return lowerKey.includes('application') && 
               (lowerKey.includes('name') || lowerKey.includes('system'));
      });
      console.log('DEBUG - AssetNamePreview: Detected column via search:', detectedColumn);
    }

    const sampleNames = parsedData.data.slice(0, 8).map((row, index) => {
      let name = '';
      if (detectedColumn && row[detectedColumn] !== undefined) {
        name = String(row[detectedColumn] || '').trim();
      }
      
      return {
        rowNumber: index + 1,
        originalValue: name,
        willBecomeValue: name && name !== '' ? name : `Asset ${index + 1}`,
        isEmpty: !name || name === '',
        columnUsed: detectedColumn
      };
    });

    console.log('DEBUG - AssetNamePreview: Sample names:', sampleNames);

    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-center space-x-2 mb-3">
          <Eye className="h-5 w-5 text-blue-600" />
          <h4 className="font-medium text-blue-900">Asset Name Preview</h4>
        </div>
        
        {detectedColumn ? (
          <div>
            <p className="text-sm text-blue-800 mb-3">
              Asset names will be taken from column: <strong>"{detectedColumn}"</strong>
            </p>
            
            <div className="bg-white border border-blue-200 rounded p-3">
              <p className="text-sm font-medium text-gray-700 mb-2">
                Sample asset names from your CSV:
              </p>
              <div className="space-y-1">
                {sampleNames.map((sample, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Row {sample.rowNumber}:</span>
                    <div className="flex items-center space-x-2">
                      {sample.isEmpty ? (
                        <>
                          <span className="text-red-600 line-through">Empty</span>
                          <ArrowRight className="h-3 w-3 text-gray-400" />
                          <span className="text-blue-600 font-medium">"{sample.willBecomeValue}"</span>
                        </>
                      ) : (
                        <span className="text-green-600 font-medium">"{sample.willBecomeValue}"</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {sampleNames.filter(s => s.isEmpty).length > 0 && (
                <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                  <strong>Note:</strong> Rows with empty asset names will get auto-generated names like "Asset 1", "Asset 2", etc.
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-red-50 border border-red-200 rounded p-3">
            <div className="flex items-center space-x-2 text-red-700">
              <AlertCircle className="h-4 w-4" />
              <p className="font-medium">No asset name column detected!</p>
            </div>
            <p className="text-sm text-red-600 mt-1">
              Asset names will be auto-generated as "Asset 1", "Asset 2", etc.
            </p>
            <p className="text-sm text-red-600 mt-1">
              <strong>Available columns:</strong> {Object.keys(parsedData.data[0] || {}).join(', ')}
            </p>
            <button
              onClick={() => setShowManualMapping(true)}
              className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Click here to manually map an asset name column
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* File Summary */}
      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">File Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded border">
            <div className="text-2xl font-bold text-blue-600">{parsedData.data.length}</div>
            <div className="text-sm text-gray-600">Total Rows</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded border">
            <div className="text-2xl font-bold text-green-600">{parsedData.headers.length}</div>
            <div className="text-sm text-gray-600">Columns</div>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded border">
            <div className="text-2xl font-bold text-purple-600">{parsedData.meta.delimiter || 'Auto'}</div>
            <div className="text-sm text-gray-600">Delimiter</div>
          </div>
        </div>
      </div>

      {/* Asset Name Preview */}
      <AssetNamePreview />

      {/* Column Mapping Results */}
      {mappingResult && mappingConfidence && (
        <div className={`border rounded-lg p-6 ${getConfidenceColor(mappingConfidence.level)}`}>
          <div className="flex items-start space-x-4">
            {getConfidenceIcon(mappingConfidence.level)}
            <div className="flex-1">
              <h3 className="text-lg font-medium">Column Mapping Analysis</h3>
              <p className="text-sm mt-1">
                {mappingConfidence.mappedCount} of {mappingConfidence.totalColumns} columns were automatically mapped 
                ({Math.round(mappingConfidence.score)}% confidence)
              </p>
              
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-3 bg-white rounded border">
                  <div className="text-xl font-bold text-green-600">{mappingConfidence.mappedCount}</div>
                  <div className="text-sm text-gray-600">Mapped Columns</div>
                </div>
                <div className="text-center p-3 bg-white rounded border">
                  <div className="text-xl font-bold text-orange-600">{mappingResult.unmappedColumns.length}</div>
                  <div className="text-sm text-gray-600">Unmapped Columns</div>
                </div>
                <div className="text-center p-3 bg-white rounded border">
                  <div className="text-xl font-bold text-blue-600">{Math.round(mappingConfidence.score)}%</div>
                  <div className="text-sm text-gray-600">Auto-Mapping Success</div>
                </div>
              </div>

              {mappingResult.unmappedColumns.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Unmapped Columns:</h4>
                  <div className="flex flex-wrap gap-2">
                    {mappingResult.unmappedColumns.map(column => (
                      <span key={column} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        {column}
                      </span>
                    ))}
                  </div>
                  <button
                    onClick={() => setShowManualMapping(true)}
                    className="mt-3 inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-100 border border-blue-200 rounded-md hover:bg-blue-200"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Manual Mapping
                  </button>
                </div>
              )}

              {mappingConfidence.recommendations.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Recommendations:</h4>
                  <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                    {mappingConfidence.recommendations.map((rec, index) => (
                      <li key={index}>{rec}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Column Mapping Details */}
      {mappingResult && (
        <div className="bg-white border rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Column Mapping Details</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">CSV Column</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">→</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Application Field</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {parsedData.headers.map(header => {
                  const mappedField = mappingResult.mapping[header];
                  const isMapped = !!mappedField;
                  return (
                    <tr key={header} className={isMapped ? "bg-green-50" : "bg-orange-50"}>
                      <td className="px-4 py-2 text-sm font-medium text-gray-900">{header}</td>
                      <td className="px-4 py-2 text-center">
                        <ArrowRight className={`h-4 w-4 ${isMapped ? 'text-green-500' : 'text-gray-400'}`} />
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900">
                        {mappedField || <span className="italic text-gray-500">Not mapped</span>}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          isMapped 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-orange-100 text-orange-800'
                        }`}>
                          {isMapped ? 'Mapped' : 'Unmapped'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Data Preview */}
      <div className="bg-white border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Data Preview</h3>
          <button
            onClick={() => setShowFullPreview(!showFullPreview)}
            className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            {showFullPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            <span>{showFullPreview ? 'Show Less' : 'Show More'}</span>
          </button>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {parsedData.headers.map(header => (
                  <th key={header} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase truncate max-w-32">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {parsedData.data.slice(0, showFullPreview ? 10 : 3).map((row, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  {parsedData.headers.map(header => (
                    <td key={header} className="px-4 py-2 text-sm text-gray-900 truncate max-w-32" title={row[header]}>
                      {row[header] || <span className="text-gray-400 italic">empty</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {parsedData.data.length > (showFullPreview ? 10 : 3) && (
          <p className="text-sm text-gray-600 mt-3 text-center">
            Showing {showFullPreview ? 10 : 3} of {parsedData.data.length} rows
          </p>
        )}
      </div>

      {/* Manual Mapping Modal */}
      <ManualMappingModal />
    </div>
  );
};

export default DataPreview; 